import { deepToRaw } from "@app-utilities/func";
import { trackUserEvent } from "@app-utilities/observability";
import { AppInsightsEvent } from "@app-utilities/observability-events";
import { persistenceKey } from "@app-utilities/reservation-flux";
import { Action, Module, Mutation, VuexModule } from "vuex-class-modules";

import store from "..";

interface IAttractionReservationTokenData {
	createdAt: Date | null,
	reservationId: number | null,
	reservationToken: string | null
}

type ReservationTokensMap = Record<string, IAttractionReservationTokenData>;

const moduleName = "attractionReservation";
const moduleStorageKey = "attraction_reservation_data";

@Module class AttractionReservationStoreFactory extends VuexModule {
	createdAt: Date | null = null;
	maxLife = 3.6e+6; // 1 hour
	moduleInitialized = false;
	reservationId: number | null = null;
	reservationToken: string | null = null;

	getReservationTokenData(): IAttractionReservationTokenData {
		return {
			createdAt: this.createdAt,
			reservationId: this.reservationId,
			reservationToken: this.reservationToken
		};
	}

	@Mutation setReservationTokenData(data: IAttractionReservationTokenData) {
		Object.keys(data).forEach(key => {
			this[key] = data[key];
		});
	}

	@Mutation setCreatedAt(createdAt: Date) {
		this.createdAt = createdAt;
	}

	@Mutation setModuleInitialized(moduleInitialized: boolean) {
		this.moduleInitialized = moduleInitialized;
	}

	@Mutation setReservationId(reservationId: number | null) {
		this.reservationId = reservationId;
	}

	@Mutation setReservationToken(reservationToken: string | null) {
		if (this.reservationToken === reservationToken) return;

		this.reservationToken = reservationToken;
	}

	private _cleanExpiredReservations() {
		const currentData: ReservationTokensMap = JSON.parse(localStorage.getItem(moduleStorageKey) ?? "{}");
		let modified = false;

		for (const [key, entry] of Object.entries(currentData)) {
			if (!entry) continue;

			if (!entry?.createdAt) {
				delete currentData[key];
				modified = true;
				continue;
			}

			const createdAt = new Date(entry.createdAt).getTime();
			const expireAt = createdAt + this.maxLife;
			const isExpired = Date.now() >= expireAt;

			if (isExpired) {
				delete currentData[key];
				modified = true;
			}
		}

		if (modified)
			localStorage.setItem(moduleStorageKey, JSON.stringify(currentData));
	}

	@Action async updateReservationTokenData({reservationId, reservationToken} : { reservationId: number, reservationToken: string | null}) {
		this._cleanExpiredReservations();

		if (!reservationToken)
			trackUserEvent(AppInsightsEvent.AttractionReservationTokenMissingOnCreate);

		this.setCreatedAt(new Date());
		this.setReservationToken(reservationToken);
		this.setReservationId(reservationId);

		const moduleKey = await persistenceKey("guest", String(reservationId));
		if(!moduleKey) return;

		const currentData: ReservationTokensMap = JSON.parse(localStorage.getItem(moduleStorageKey) ?? "{}");
		currentData[moduleKey] = deepToRaw(this.getReservationTokenData());
		localStorage.setItem(moduleStorageKey, JSON.stringify(currentData));
	}

	private async _syncWithStorage() {
		const moduleKey = await persistenceKey("guest", String(this.reservationId));
		if (!moduleKey || !this.reservationId) return;

		const currentData: ReservationTokensMap = JSON.parse(localStorage.getItem(moduleStorageKey) ?? "{}");
		const result = currentData[moduleKey];

		const data: IAttractionReservationTokenData = result ?? {
			createdAt: null,
			reservationId: null,
			reservationToken: null,
		};

		this.setReservationTokenData(data);
	}

	@Action async initModuleData() {
		if(this.moduleInitialized) return;

		this._cleanExpiredReservations();
		await this._syncWithStorage();

		this.setModuleInitialized(true);
	}
}
let AttractionReservationStore: AttractionReservationStoreFactory | null;
function useAttractionReservationStore() {
	if (AttractionReservationStore) return AttractionReservationStore;

	const mod = AttractionReservationStore = new AttractionReservationStoreFactory({ store, name: moduleName });

	return mod;
}

export default useAttractionReservationStore;
